import * as React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import OutlinedInput from '@mui/material/OutlinedInput';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Eye as EyeIcon } from '@phosphor-icons/react/dist/ssr/Eye';
import { EyeSlash as EyeSlashIcon } from '@phosphor-icons/react/dist/ssr/EyeSlash';
import { Controller, useForm } from 'react-hook-form';
import { z as zod } from 'zod';
import toast from 'react-hot-toast';
import axios from '../../../services/ApiService';
import { paths } from '../../../utils/paths';
import LoadingSpinner from '../../support/LoadingSpinner';
import { useAuthHeader } from '../../../hooks/useAuthHeader';
import { useAuthContext } from '../../../hooks/useAuthContext';

// Validation schema
const schema = zod.object({
  email: zod.string().min(1, { message: 'Email is required' }).email(),
  password: zod.string().min(1, { message: 'Password is required' }).optional(),
  otp: zod.string()
    .min(6, { message: 'OTP must be 6 digits' })
    .max(6, { message: 'OTP must be 6 digits' })
    .regex(/^\d+$/, { message: 'OTP must contain only numbers' })
    .optional(),
});

type Values = zod.infer<typeof schema>;

export function SignInForm(): React.JSX.Element {
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState(false);
  const [emailError, setEmailError] = React.useState<string>('');
  const [passwordError, setPasswordError] = React.useState<string>('');
  const navigate = useNavigate();
  const { setAuthHeader } = useAuthHeader();
  const { dispatch } = useAuthContext();
  const [otpLogin, setOtpLogin] = React.useState<boolean>(false);

  const { control, handleSubmit, setError, formState: { errors } } = useForm<Values>({ resolver: zodResolver(schema) });

  const onSubmit = React.useCallback(
    async (values: Values): Promise<void> => {
      setEmailError('');
      setPasswordError('');
      let isValid = true;

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!values.email) {
        setEmailError("Email is required.");
        isValid = false;
      } else if (!emailRegex.test(values.email)) {
        setEmailError("Please enter a valid email address.");
        isValid = false;
      }

      // Validation based on login type
      if (otpLogin) {
        if (!values.otp) {
          setPasswordError("OTP is required.");
          isValid = false;
        }
      } else {
        if (!values.password) {
          setPasswordError("Password is required.");
          isValid = false;
        } else if (values.password.length < 6) {
          setPasswordError("Password must be at least 6 characters.");
          isValid = false;
        }
      }

      // Proceed with login if valid
      if (isValid) {
        const cred = {
          userId: values.email,
          ...(otpLogin 
            ? { otp: values.otp }
            : { password: values.password }
          ),
        };

        setLoading(true);
        try {
          const response = await axios.post("/login", cred);
          setAuthHeader(response.data.token);
          dispatch({ type: 'LOGIN', payload: response.data });
          setLoading(false);
          navigate('/', {replace: true});
        } catch (error) {
          setLoading(false);
          if (error.status === 400) {
            setPasswordError(otpLogin ? "Invalid OTP!" : "Invalid Credentials!");
          } else if (error.status === 404) {
            setEmailError("User Not Found!");
          } else {
            toast.error("Could not Login, Please Try Again!");
          }
        }
      }
    },
    [setError, otpLogin, navigate, setAuthHeader, dispatch]
  );

  const handleSendOtp = React.useCallback(async () => {
    const email = control._getWatch('email'); // Get current email value

    if (!email) {
      setEmailError('Email is required to send OTP');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    const data = {
      email: email
    }
    try {
      await axios.post('/generate-login-otp', data);
      toast.success('OTP sent successfully!');
    } catch (error) {
      if (error.status === 404) {
        setEmailError('User not found');
      } else {
        toast.error('Failed to send OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [control]);

  return (
    <Stack spacing={4}>
      <Stack spacing={1}>
        <Typography variant="h4">Sign in</Typography>
        <Typography color="text.secondary" variant="body2">
          Don&apos;t have an account?{' '}
          <Link component={RouterLink} to={paths.auth.signup} underline="hover" variant="subtitle2">
            Sign up
          </Link>
        </Typography>
      </Stack>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <FormControl fullWidth error={Boolean(errors.email || emailError)}>
                <InputLabel size='normal'>Email address</InputLabel>
                <OutlinedInput {...field} label="Email address" type="email" size='medium' />
                {errors.email || emailError ? <FormHelperText>{errors.email?.message || emailError}</FormHelperText> : null}
              </FormControl>
            )}
          />
          {otpLogin ? (
            <FormControl fullWidth error={Boolean(errors.otp || passwordError)}>
              <InputLabel size='normal'>OTP</InputLabel>
              <Stack direction="row" spacing={1}>
                <Controller
                  control={control}
                  name="otp"
                  render={({ field }) => (
                    <OutlinedInput
                      {...field}
                      label="OTP"
                      type="text"
                      size='medium'
                      fullWidth
                    />
                  )}
                />
                <Button 
                  variant="outlined"
                  size='medium' 
                  onClick={handleSendOtp}
                  disabled={loading}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Send OTP
                </Button>
              </Stack>
              {(errors.otp || passwordError) && (
                <FormHelperText>{errors.otp?.message || passwordError}</FormHelperText>
              )}
            </FormControl>
          ) : (
            <Controller
              control={control}
              name="password"
              render={({ field }) => (
                <FormControl fullWidth error={Boolean(errors.password || passwordError)}>
                  <InputLabel size='normal'>Password</InputLabel>
                  <OutlinedInput
                    {...field}
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    size='medium'
                  />
                  {errors.password || passwordError ? (
                    <FormHelperText>{errors.password?.message || passwordError}</FormHelperText>
                  ) : null}
                </FormControl>
              )}
            />
          )}
          {otpLogin ? (
            <div>
              <Link component={RouterLink} to="#" onClick={() => setOtpLogin(false)} variant="subtitle2">
                Login Using Password
              </Link>
            </div>
          ):(
            <div>
              <Link component={RouterLink} to="#" onClick={() => setOtpLogin(true)} variant="subtitle2">
                Login Using OTP
              </Link>
            </div>
          )}
          {errors.root ? <Alert color="error">{errors.root.message}</Alert> : null}
          <Button disabled={loading} type="submit" variant="contained" size='medium'>
            Sign in
          </Button>
        </Stack>
      </form>
      <LoadingSpinner loading={loading} />
    </Stack>
  );
}
